import {Chart, ChartAxisXOptions, ChartAxisYOptions, ChartDataPoint, ChartDataSeriesOptions, ChartEvent, ChartOptions, ChartRangeEvent, ChartStripLinesOptions, ChartToolTipOptions} from 'canvasjs';
import moment, {Moment} from 'moment-timezone';
import {AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ResizeEvent} from 'angular-resizable-element';
import {MessageTranslateService} from '@shared/services';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {MathUtils} from '@shared/util/general/math-utils';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {SearchUtils} from '@shared/util/general/search-utils';
import {HtmlUtils} from '@shared/util/general/html-utils';
import {I18nMessage} from '@shared/api/model/i18n';
import {AppConstants} from "@shared/util/app-constants";
import {ChartsUtil, IsoWeekday, SplitMode} from '../charts-util';
import {ChartTimestampUtils} from '../chart-timestamp-utils';
import {FavouriteId, WorkingHours} from '@app/graphql-typings/graphql';
import {Orientation, ReferenceLine} from '../chart-controls/reference-line';
import {UnitUtilsService} from '@shared/services/util/unit-utils.service';
import {DataTableComponent} from '../data-table/data-table.component';
import {FavouriteData} from '@app/layout/charts/charts-util';
import {SiteInfo} from '@app/shared/services/api/access-sites.interface';
import {ChartResizeEvent, LabelChartDataPoint, RESIZE_EVENT_STATUS} from '@shared/components/canvas-chart/canvas-chart.component';
import {DataInterval} from '@app/layout/sitemanager/meters/meters.service';
import {DLSInfo} from '@shared/util/datetime/timezone-util';
import {ChartService} from '../chart.service';
import {CsvExportService} from "@shared/services/util/csv-export.service";
import {ColumnOption} from "csv-stringify/lib";
import {ResizeObservable} from '@app/shared/util/dom/resize.observable';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {ScheduleUtil} from '../schedule-util';
import {ChartComponent} from "@shared/model/chart-component.interface";
import {CSVPivotConfig, CSVPivotData, ExportDataBuilder, MiniChartDataSeriesOptions, ValueBasedPivotConfig, ViewportBounds} from "@app/layout/charts/mini-chart/export-data-builder";
import {PeriodUtilService} from "@shared/services/util/period-util.service";
import {TimestampUtils} from '@shared/services/util/timestamp-utils';
import {ALERT_FAULT, ALERT_NON_FAULT, ChartSeriesDataType, MeterHistoryChartSeriesData, ValueBasedXAxisInfo} from '@app/layout/charts/mini-chart/mini-chart.interface';
import {WorkHoursWeekdayBoundsConfig} from '@app/layout/charts/schedule-util';
import {IntervalPromptModalComponent} from '../interval-prompt-modal/interval-prompt-modal.component';


export interface SelectableOption {
  label: string;
  value: string;
}


interface TimestampInfo {
  ts: Moment;
  info: {
    dlsInfo: DLSInfo;
    leapYearInfo: {leapYear: boolean};
  };
}

interface XAxisTooltipInfo {
  favouriteData: FavouriteData;
  isXAxisFavouriteBinaryUnit: boolean;
  displayedUnitName: string;
  binSize: number;
}

interface MultiTooltipDetails {
  colour: string;
  htmlEscapedSymbol: string; // NOTE: The symbol is printed as is to HTML, so needs to be HTML-encoded before assigning to this property
  equipmentInfo: string;
  metadataInfo: string;
  functionInfo: string;
  zoneInfo: string;
  timestampInfo: TimestampInfo;
  xValueInfo: string;
  valueInfo: string;
  sampleSizeInfo: string | null;
  varianceInfo: {
    info: string;
    qualifier?: string;
  }[];
  highlight: boolean;
  estimateInfo: string;
}

enum SYMBOL {
  DASH = '&mdash;',
  SQUARE = '&#9724;'
}

export enum JS_CHART_SERIES_TYPE {
  AREA = 'area',
  LINE = 'line',
  BAR = 'bar',
  COLUMN = 'column',
  STACKED = 'stackedColumn'
}


enum CHART_CSS_CLASS {
  DYNAMIC = 'dynamicChartContainer',
  SPLIT = 'splitChartContainer',
  BINARY_ONLY = 'binaryOnlyChartContainer',
  BINARY_AND_ALERT = 'binaryAndAlertsChartContainer',
  FULL_BROWSER_HEIGHT = 'fullBrowserHeightChartContainer'
}

export enum CHART_HEIGHT {
  DYNAMIC,
  SPLIT,
  BINARY_ONLY,
  BINARY_AND_ALERT,
  FULL_BROWSER_HEIGHT
}

@Component({
  selector: 'app-mini-chart',
  templateUrl: './mini-chart.component.html',
  styleUrls: ['./mini-chart.component.scss'],
  standalone: false
})

export class MiniChartComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy, ChartComponent {
  @Input() chartOptions: ChartOptions = <ChartOptions>{};
  @Input() startDate: Moment;
  @Input() endDate: Moment;
  @Input() site: SiteInfo;
  // Use an object instead of a plain boolean so that it is possible to trigger an OnChanges event even if the value
  // of the boolean does not change. Used to call chart.render() to show the latest data (in chartOptions.data) while
  // the data is loaded progressively, as Angular change detection does not pickup a change when an array (in this case
  // chartOptions.data) is modified, without rebuilding the array
  @Input() loading: {loading: boolean};
  @Input() splitMode: any;
  @Input() allowedSplitModes: Set<any> = new Set();
  @Input() menuItemLabels: Map<any, {label: I18nMessage, tooltip: I18nMessage}>;
  @Input() referenceLines: ReferenceLine[];
  @Input() workingHours: WorkingHours;
  @Input() chartHeight: string;
  @Input() defaultHeights: Map<CHART_HEIGHT, string>;
  @Input() favouritesById: Map<FavouriteId, FavouriteData>;
  @Input() fullBrowserHeight: boolean;
  @Input() hideMenu = false;
  @Input() externalResizeInProgress = false;
  @Input() xAxisLabelFormatter: (e: any) => string;
  @Input() yAxisLabelFormatter: (e: any) => string;
  @Input() availableChartTypes: JS_CHART_SERIES_TYPE[];
  @Input() chartType: JS_CHART_SERIES_TYPE = JS_CHART_SERIES_TYPE.LINE;
  @Input() showEstimateFlags = false;
  @Input() chartHasEstimates = false;
  @Input() availableTimezones: SelectableOption[];
  @Input() timezone: string;
  @Input() showVariance: boolean;
  @Input() comparePreviousOffset: moment.Duration;
  @Input() valueBasedXAxisInfo: ValueBasedXAxisInfo | null;

  @Output() splitModeEvent = new EventEmitter<any>();
  @Output() rangeChangedEvent = new EventEmitter<any>();
  @Output() resizeEvent = new EventEmitter<ChartResizeEvent>();
  @Output() selectedChartTypeEvent = new EventEmitter<JS_CHART_SERIES_TYPE>();
  @Output() showEstimateFlagsEvent = new EventEmitter<boolean>();
  @Output() selectedTimezone = new EventEmitter<string>();
  @Output() chartCompleted = new EventEmitter<void>();

  private readonly ngUnsubscribe = new Subject<void>();
  private readonly TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

  private readonly WORKING_HOURS_COLOR = '#E5E5E5';

  private readonly CHART_MIN_HEIGHT_PX = 100;
  private readonly RESIZE_SCROLL_MARGIN = 20;
  private readonly RESIZE_DEFAULT_SCROLL_OFFSET = 56; // The height of the 'fixed' bar at the top of the page
  private readonly NOTE_MARKER_HTML = '<sup>*</sup>';
  /** Number of columns in the tooltip table. */
  private readonly TOOLTIP_COLUMN_COUNT = 10;
  /** Columns the timezone row's message spans. */
  private readonly TOOLTIP_TIMEZONE_MESSAGE_SPAN = 5;
  private readonly SAMPLE_SIZE_LABEL = 'n';

  private readonly defaultChartHeights = new Map([[CHART_HEIGHT.DYNAMIC, '81vh'], [CHART_HEIGHT.SPLIT, '175px'],
    [CHART_HEIGHT.FULL_BROWSER_HEIGHT, '100vh'], [CHART_HEIGHT.BINARY_ONLY, '75px'], [CHART_HEIGHT.BINARY_AND_ALERT, '100px']]);
  private chartHeights = this.buildChartHeights(null);

  private readonly defaultAxisXOption: ChartAxisXOptions = <ChartAxisXOptions>{
    crosshair: {
      enabled: true,
      snapToDataPoint: true,
      labelFontSize: 9,
      labelFormatter: (e: any) => e.value instanceof Date
        ? moment(e.value.getTime()).tz(this.effectiveTimezone).format('HH:mm')
        : e.value
    },
    labelFormatter: (e: {chart: Chart, axis: ChartAxisYOptions, value: number, label: string}) => {
      const viewportRange = e.chart.axisX[0].viewportMaximum - e.chart.axisX[0].viewportMinimum;
      const labelFormat = ChartTimestampUtils.axisXScaledLabel(viewportRange);
      const ts = moment.tz(e.value, this.effectiveTimezone);
      const dlsMessage = ChartTimestampUtils.getDLSMessage(ChartTimestampUtils.getDLSInfoToDisplay(ts, null));
      const label = ts.format(labelFormat);
      if (dlsMessage != null && labelFormat === ChartTimestampUtils.TIME_ONLY_FORMAT) {
        return label + ' ' + this.translateService.instant(dlsMessage);
      } else {
        return label;
      }
    },
    interval: null,
    intervalType: null,
    labelAngle: -20,
    fontWeight: 'normal',
    labelFontSize: 11,
    labelMaxWidth: 170,
    labelWrap: true,
  };
  private readonly valueBasedXAxisOptions: ChartAxisXOptions = {
    ...this.defaultAxisXOption,
    labelFormatter: null,
    labelAngle: null
  };

  private readonly defaultAxisYOption: ChartAxisYOptions = <ChartAxisYOptions> {
    crosshair: {
      enabled: false
    },
    fontWeight: 'normal',
    valueFormatString: '#.#',
    labelFontSize: 11,
  };

  private readonly defaultAxisY2Option: ChartAxisYOptions = {
    lineThickness: 0,
    tickThickness: 0,
    valueFormatString: ' ',
    maximum: 1,
    minimum: 0
  };

  private readonly logger: AceLogger;

  private exportDataBuilder: ExportDataBuilder;

  @ViewChild('chartContainer', { static: true }) chartContainer: ElementRef;
  @ViewChild('chartTypePrompt', { static: true }) chartTypePrompt: ElementRef;
  @ViewChild('timezonePrompt', { static: true }) timezonePrompt: ElementRef;

  chart: Chart;
  chartClass: CHART_CSS_CLASS = CHART_CSS_CLASS.DYNAMIC;
  allowResize = true;
  availableChartTypeOptions: SelectableOption[];

  private chartLoading = true;
  // Need to delay calling chart.render() after resize, until the view actually resized the chart container (i.e. div that holds the chart),
  // so that the chart can lookup the container's height. So need a variable to record that the resize has happened, so that chart.render()
  // can be called in ngAfterViewChecked(), i.e. after the view has been updated
  private sizeChanged = false;
  private resizeInProgress = false;
  private resizeStartCoordsTopOffset: number = null;
  private chartTitle: string;
  // Used to delay creating a Full Size Chart Menu item creation until the chart is created
  private createFullSizeChartMenuItem = false;

  private effectiveTimezone: string;
  private workingHourBoundsByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig> | null;

  constructor(private translateService: MessageTranslateService,
              private modalService: NgbModal,
              private unitUtilsService: UnitUtilsService,
              private csvExportService: CsvExportService,
              private periodUtilService: PeriodUtilService,
              private logService: AceLoggerService,
              private chartService: ChartService) {
    this.logger = logService.create(MiniChartComponent.name);
  }

  ngOnInit() {
    // Set the chart height only if it has not been explicitly specified as an input
    if (this.chartHeight == null) {
      this.chartHeight = this.getChartHeightForKey(CHART_HEIGHT.DYNAMIC);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.site || changes.timezone) {
        this.effectiveTimezone = this.timezone ?? this.site?.timezone;
      }

      if (changes.site || changes.timezone || changes.comparePreviousOffset) {
        this.exportDataBuilder = new ExportDataBuilder(this.effectiveTimezone, this.periodUtilService, this.translateService, this.comparePreviousOffset, this.logService);
      }

      if (changes.allowedSplitModes && !this.allowedSplitModes) {
        this.allowedSplitModes = new Set();
      }

      if (changes.workingHours || changes.site) {
        this.workingHourBoundsByWeekday = this.workingHours ? ScheduleUtil.buildWorkingHoursConfigByWeekday(this.workingHours) : null;
      }

      if (changes.defaultHeights) {
        let shouldUpdateHeight = false;
        if (!changes.chartOptions && !changes.chartHeight && this.chartHeight != null && this.chartClass != null) {
          const currentChartHeightType = this.getChartHeightTypeFromChartClass(this.chartClass);
          // Check if the non-standard defaults contain currentChartHeight, if not, no need to update the height of the chart
          if (this.defaultHeights.has(currentChartHeightType)) {
            const previousDefaultHeight = this.getChartHeightForKey(currentChartHeightType);
            // Only update the height of the chart if it is the default for current chart height type
            shouldUpdateHeight = this.chartHeight === previousDefaultHeight;
          }
        }

        this.chartHeights = this.buildChartHeights(this.defaultHeights);
        if (shouldUpdateHeight) {
          // Lookup the new height after the custom height default have been applied
          this.chartHeight = this.getChartHeightForKey(this.getChartHeightTypeFromChartClass(this.chartClass));
        }
      }

      if (changes.chartOptions) {
        let chartHeight = CHART_HEIGHT.DYNAMIC;
        if (this.chartOptions && this.chartOptions.data && this.chartOptions.data.length) {

          let axisXOption: ChartAxisXOptions = {
            ...this.valueBasedXAxisInfo != null ? this.valueBasedXAxisOptions : this.defaultAxisXOption,
            ...this.xAxisLabelFormatter && {labelFormatter: this.xAxisLabelFormatter}
          };
          const optionsAssignFn = (defaultOptions: ChartAxisYOptions, axisYOptions: ChartAxisYOptions) => Object.assign({}, defaultOptions, axisYOptions);
          const optionInitFn = (defaultOptions: ChartAxisYOptions, axisYOptions: ChartAxisYOptions | ChartAxisYOptions[]) => {
            return Array.isArray(axisYOptions) ? axisYOptions.map(axisY => optionsAssignFn(defaultOptions, axisY)) : optionsAssignFn(defaultOptions, axisYOptions);
          };
          let axisYOption = optionInitFn({...this.defaultAxisYOption, ...this.yAxisLabelFormatter && {labelFormatter: this.yAxisLabelFormatter}}, this.chartOptions.axisY);
          let axisY2Option = optionInitFn(this.defaultAxisY2Option, this.chartOptions.axisY2);

          let addBinaryStripLine = false;

          if (this.splitMode) {
            if (this.fullBrowserHeight) {
              this.logger.error('Chart cannot have Full Browser Height in Split Mode. Using default dynamic height');
              this.fullBrowserHeight = false;
            }

            chartHeight = CHART_HEIGHT.SPLIT;
            this.chartClass = CHART_CSS_CLASS.SPLIT;
            this.allowResize = true;
          } else {
            if (this.fullBrowserHeight) {
              chartHeight = CHART_HEIGHT.FULL_BROWSER_HEIGHT;
              this.chartClass = CHART_CSS_CLASS.FULL_BROWSER_HEIGHT;
              this.allowResize = false;
            } else {
              chartHeight = CHART_HEIGHT.DYNAMIC;
              this.chartClass = CHART_CSS_CLASS.DYNAMIC;
              this.allowResize = true;
            }
          }

          const titlesData = {equipmentNames: new Set<string>(), unitNames: new Set<string>(), meterNames: new Set<string>(), metadataNames: new Set<string>()};
          // Records unique set of favourite data series (i.e. works across chart functions for the same favourite)
          const favouriteIds: Set<FavouriteId> = new Set();
          this.chartOptions.data.forEach(series => {
            const seriesData = this.getChartSeriesDataType(series);
            series.showInLegend = true;
            if (ChartSeriesDataType.isPointHistory(seriesData)) {
              favouriteIds.add(+seriesData.id);
              series.showInLegend = true;
              const unitName = this.favouritesById.get(+seriesData.id).getUnitName();
              titlesData.metadataNames.add(seriesData.name);
              titlesData.equipmentNames.add(seriesData.equipmentName);
              titlesData.unitNames.add(unitName);
              if (this.shouldShowAsBinary(unitName)) {
                if (this.splitMode) {
                  series.type = JS_CHART_SERIES_TYPE.LINE;
                  this.allowResize = false;
                  addBinaryStripLine = true;
                  const multipleSeries = this.chartOptions.data.length > 1;
                  if (multipleSeries) {
                    chartHeight = CHART_HEIGHT.BINARY_AND_ALERT;
                    this.chartClass = CHART_CSS_CLASS.BINARY_AND_ALERT;
                  } else {
                    chartHeight = CHART_HEIGHT.BINARY_ONLY;
                    this.chartClass = CHART_CSS_CLASS.BINARY_ONLY;
                  }

                  Object.assign(series, {type: JS_CHART_SERIES_TYPE.AREA, legendMarkerType: 'square', fillOpacity: 0.6, axisYIndex: 0,
                    axisYType: 'primary', showInLegend: multipleSeries});

                  axisXOption = Object.assign(axisXOption, {labelAngle: -1, labelFormatter: () => 'fill', labelFontSize: 11, labelFontColor: 'transparent',
                    gridThickness: 0, lineThickness: 0, tickThickness: 0});
                  axisYOption = Object.assign(axisYOption, {gridThickness: 0, lineThickness: 0, tickThickness: 0, minimum: 0.1, maximum: 0.9, labelFormatter: () => ''});
                  axisY2Option = Object.assign(axisY2Option, axisYOption);
                } else {
                  series.type = this.chartType;
                  if (this.chartType === JS_CHART_SERIES_TYPE.STACKED) {
                    series.axisYIndex = 0;
                    series.axisYType = 'primary';
                  } else {
                    series.axisYIndex = 1;
                    series.axisYType = 'secondary';
                  }
                }
              } else {
                series.type = this.chartType;
                series.axisYIndex = 0;
                series.axisYType = 'primary';
              }
            } else if (ChartSeriesDataType.isAlertHistory(seriesData)) {
              Object.assign(series, {type: JS_CHART_SERIES_TYPE.AREA, legendMarkerType: 'square', fillOpacity: 0.3, includeZero: true, axisYType: 'secondary', showInLegend: true});
            } else if (ChartSeriesDataType.isMeterHistory(seriesData)) {
              titlesData.meterNames.add(seriesData.name);
              if (seriesData.forceChartType != null) {
                series.type = seriesData.forceChartType.type;
                if (seriesData.forceChartType.markerType) {
                  series.markerType = seriesData.forceChartType.markerType;
                }
              } else {
                series.type = this.chartType;
              }
              series.showInLegend = true;
            } else {
              this.logger.error('Invalid series type "' + seriesData['type'] + '"');
            }
          });

          // Workout the Chart Title and set it to a variable. The title will be actually set on the chart when the data loading is completed
          if (this.valueBasedXAxisInfo != null) {
            const xAxisFavourite = this.favouritesById.get(this.valueBasedXAxisInfo.favouriteId);
            const xAxisFavouriteName = xAxisFavourite?.favourite.metadata.name ?? AppConstants.SCORE_CARD_LOADING_PLACEHOLDER;
            const xAxisEquipmentName = this.valueBasedXAxisInfo.equipment.name ?? '';
            const xAxisName = xAxisFavourite
              ? (xAxisEquipmentName + ' ' + xAxisFavouriteName)
              : AppConstants.SCORE_CARD_LOADING_PLACEHOLDER;
            const favouriteNames = Array.from(titlesData.metadataNames).join(', ');
            const formattedStartDate = this.startDate.format(AppConstants.DATE_FORMAT);
            const formattedEndDate = TimestampUtils.exclusiveToInclusive(this.endDate, TimestampUtils.INCLUSIVITY_DAY_SHIFT_DURATION).format(AppConstants.DATE_FORMAT);
            const dateRange = (formattedStartDate === formattedEndDate)
              ? formattedStartDate
              : (formattedStartDate + ' - ' + formattedEndDate);
            this.chartTitle = this.translateService.instant({messageKey: 'vs', parameters: {item1: xAxisName, item2: favouriteNames}}) + ', ' + dateRange;
          } else if (favouriteIds.size === 1) {
            const favData = this.favouritesById.get(favouriteIds.values().next().value);
            const zoneInfo = favData.favourite.zone.system ? '' : ' ' + favData.favourite.zone.level.level_name + ' ' + favData.favourite.zone.zone_name.zone_name;
            this.chartTitle = titlesData.equipmentNames.values().next().value + ' - ' + favData.favourite.metadata.name + zoneInfo + ' - ' + favData.getUnitName();
          } else if (favouriteIds.size > 1) {
            const unitInfo = this.splitMode === SplitMode.BY_UNIT ? Array.from(titlesData.unitNames).join(', ') + ' - ' : '';
            this.chartTitle = unitInfo + Array.from(titlesData.equipmentNames).join(', ');
          } else if (titlesData.meterNames.size > 0) {
            this.chartTitle = Array.from(titlesData.meterNames).join(', ');
          } else {
            this.logger.error('Cannot set Chart Title as there are no favourite data series or meter names on the chart');
          }

          this.addGeneralChartOptions(this.chartOptions, axisXOption, axisYOption, axisY2Option, this.hideMenu);
          this.setWorkingHoursAndReferenceLinesStripLines(this.chartOptions, addBinaryStripLine);
        } else {
          this.chartTitle = null;
        }

        // Set the chart height only if it has not been explicitly specified as an input
        if (this.chartHeight == null || !changes.chartHeight) {
          this.chartHeight = this.getChartHeightForKey(chartHeight);
        } else {
          if (chartHeight === CHART_HEIGHT.DYNAMIC) {
            // If the chart height was not overridden, the chart height would be dynamic, so add a menu item to restore height to dynamic
            if (this.chart) {
              this.addMenuOptionForFullSize();
            } else {
              // As chart has not been created yet, delay creating the menu item
              this.createFullSizeChartMenuItem = true;
            }
          } else if (this.chart) {
            this.removeMenuOptionForFullSize(this.getChartMenu());
          }
        }
      } else if (changes.chartHeight && this.chartHeight == null) {
        if (!this.splitMode && !this.fullBrowserHeight) {
          // This should only happen when using a single chart which is not in Full Browser Height mode
          this.chartHeight = this.getChartHeightForKey(CHART_HEIGHT.DYNAMIC);
        } else {
          // This only happens while a change is in flight, i.e. a user has chosen to split the charts.
          // The charts will be recreated anyway, so ignoring the change.
        }
      }

      if (changes.loading) {
        this.refreshChart(!!this.loading?.loading);
      }
    }
  }

  ngAfterViewInit(): void {
    // Build the chart after the view has been initialised, as the chart will take the height of it's container if the container has a
    // defined height. This allows to avoid duplicating setting the height on both the container as well as the chart, and allows the chart
    // to dynamically adjust its height based on the height of the container. However, for this to work, the container needs to exist in the
    // view before the chart is created.
    this.chart = this.chartService.buildChart(this.chartContainer.nativeElement, this.chartOptions);
    this.chart.render();
    this.listenForResizeEvents();
    this.checkAndFinaliseChart(this.chartLoading);
  }

  ngAfterViewChecked(): void {
    if (this.sizeChanged) {
      this.sizeChanged = false;
      if (this.chart) {
        this.chart.render();
      }
    }
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy');
    if (this.chart) {
      this.chart.destroy();
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onResizeStart(event: ResizeEvent): void {
    this.resizeInProgress = true;
    this.resizeEvent.emit({status: RESIZE_EVENT_STATUS.STARTED, height: event.rectangle.height});
  }

  onResize(event: ResizeEvent): void {
    const bottomBoundary = window.scrollY + window.innerHeight - this.RESIZE_SCROLL_MARGIN;
    if (event.rectangle.bottom > bottomBoundary) {
      const scrollDistance = event.rectangle.bottom - bottomBoundary;
      window.scrollTo(window.scrollX, window.scrollY + scrollDistance);
    } else {
      const topBoundary = window.scrollY + this.getResizeCoordsTopOffset() + this.RESIZE_SCROLL_MARGIN;
      if (event.rectangle.bottom < topBoundary) {
        const scrollDistance = event.rectangle.bottom - topBoundary;
        window.scrollTo(window.scrollX, window.scrollY + scrollDistance);
      }
    }
  }

  onResizeEnd(event: ResizeEvent): void {
    this.chartHeight = event.rectangle.height + 'px';
    this.resizeInProgress = false;
    this.sizeChanged = true;
    this.addMenuOptionForFullSize();
    this.resizeEvent.emit({status: RESIZE_EVENT_STATUS.STOPPED, height: event.rectangle.height});
  }

  validateResize = (event: ResizeEvent): boolean => {
    return event.rectangle.height >= this.CHART_MIN_HEIGHT_PX;
  };

  private getChartSeriesDataType(series: ChartDataSeriesOptions): ChartSeriesDataType {
    return ChartSeriesDataType.parse(series.name);
  }

  /** Only a raw 0 or 1 can be drawn as an on/off strip. In Favourite X-Axis mode the plotted value is a bin aggregate. */
  private shouldShowAsBinary(unitName: string): boolean {
    return this.valueBasedXAxisInfo == null && this.unitUtilsService.isBinaryUnit(unitName);
  }

  private buildChartHeights(overrides: Map<CHART_HEIGHT, string>): Map<CHART_HEIGHT, string> {
    const chartHeights = new Map(this.defaultChartHeights);
    overrides?.forEach((v, k) => chartHeights.set(k, v));
    return chartHeights;
  }

  private getChartHeightForKey(key: CHART_HEIGHT): string {
    const height = this.chartHeights.get(key);
    if (height == null) {
      throw new Error('Invalid Chart Height Key ' + key);
    }
    return height;
  }

  private tooltipContentFormatter = (e: {chart: Chart, toolTip: ChartToolTipOptions,
    entries: Array<{ dataPoint: ChartDataPoint, dataSeries: ChartDataSeriesOptions }>}) => {

    // Do not show a tooltip if this or another chart is being resized
    if (this.isResizeInProgress()) {
      return null;
    }

    const targetSeriesName = e.entries[0].dataSeries.name;
    const sourceDataPoints = e.entries[0].dataSeries.dataPoints;
    const sourcePoint = e.entries[0].dataPoint;
    const targetTs = moment(this.getX(sourcePoint)).valueOf();
    const aggregatePeriod: DataInterval = e.chart?.options?.data?.[0]?.name
      ? JSON.parse(e.chart.options.data[0].name)?.aggregatePeriod
      : null;
    const isXAxisValueBased = this.valueBasedXAxisInfo != null;
    const xAxisFavData = isXAxisValueBased ? this.favouritesById.get(this.valueBasedXAxisInfo.favouriteId) : null;
    const xAxisUnitName = xAxisFavData?.getUnitName() ?? null;
    const xAxisTooltipInfo: XAxisTooltipInfo = isXAxisValueBased
      ? {
        favouriteData: xAxisFavData,
        isXAxisFavouriteBinaryUnit: xAxisUnitName ? this.unitUtilsService.isBinaryUnit(xAxisUnitName) : false,
        displayedUnitName: xAxisUnitName ?? AppConstants.SCORE_CARD_LOADING_PLACEHOLDER,
        binSize: this.valueBasedXAxisInfo.binSize
      }
      : null;

    const multiToolTipDetails: MultiTooltipDetails[] = [];
    let hasEstimates = false;
    let hasSampleSizes = false;
    e.chart.options.data.forEach(series => {
      if (series.visible) {
        const targetPointDetails = this.getChartSeriesDataType(series);
        let unitName = '';
        let labelAsBinaryUnit = false;
        let functionInfo = '';
        if (ChartSeriesDataType.isPointHistory(targetPointDetails)) {
          if (targetPointDetails.functionNameStr && targetPointDetails.functionNameStr.trim().length) {
            functionInfo = targetPointDetails.functionNameStr;
            if (targetPointDetails.timePeriodStr && targetPointDetails.timePeriodStr.trim().length) {
              functionInfo += ' (' + targetPointDetails.timePeriodStr + ')';
            }
          }
          functionInfo = ChartsUtil.applyAggregationToFunctionLabel(functionInfo, targetPointDetails.aggregationName ?? null);
        }

        let targetTooltipDetails: MultiTooltipDetails = {colour: series.color, htmlEscapedSymbol: series.type === JS_CHART_SERIES_TYPE.AREA ? SYMBOL.SQUARE : SYMBOL.DASH,
          equipmentInfo: '', metadataInfo: '', zoneInfo: '', functionInfo: '', timestampInfo: null, xValueInfo: '', valueInfo: '', sampleSizeInfo: null,
          varianceInfo: null, highlight: false, estimateInfo: null};

        if (ChartSeriesDataType.isPointHistory(targetPointDetails)) {
          const favData = this.favouritesById.get(+targetPointDetails.id);
          unitName = favData.getUnitName();
          labelAsBinaryUnit = this.shouldShowAsBinary(unitName);
          targetTooltipDetails = Object.assign(targetTooltipDetails, {
            equipmentInfo: targetPointDetails.equipmentName,
            metadataInfo: favData.favourite.metadata.name,
            zoneInfo: favData.favourite.zone.system ? '' : favData.favourite.zone.level.level_name + ' ' + favData.favourite.zone.zone_name.zone_name,
            functionInfo,
            highlight: series.name === targetSeriesName
          });
        } else if (ChartSeriesDataType.isAlertHistory(targetPointDetails)) {
          targetTooltipDetails.metadataInfo = targetPointDetails.templateName + ' - ' + this.translateService.instant('Rule ID') + ': ' + targetPointDetails.id;
        } else if (ChartSeriesDataType.isMeterHistory(targetPointDetails)) {
          targetTooltipDetails = Object.assign(targetTooltipDetails, {
            equipmentInfo: targetPointDetails.name,
            highlight: series.name === targetSeriesName
          });
        } else {
          this.logger.error('Unknown chart series type "' + targetPointDetails['type'] + '"');
        }

        const targetDataPoint = this.getClosestChartDataPoint(series.dataPoints, targetTs, sourcePoint, sourceDataPoints, isXAxisValueBased);
        if (targetDataPoint) {
          // On time based x axis, set `timestampInfo` so that it shows. Otherwise, do not set so it doesn't show
          const xValue = this.getX(targetDataPoint);
          // Data Points used for padding do not have an xValue
          if (xValue != null) {
            if (isXAxisValueBased) {
              targetTooltipDetails.xValueInfo = this.buildBinValueInfo(<number>xValue, xAxisTooltipInfo);
            } else {
              const ts = moment.tz(xValue, this.effectiveTimezone);
              targetTooltipDetails.timestampInfo = {ts: ts, info: ChartTimestampUtils.getTimestampInfoToDisplay(ts, aggregatePeriod)};
            }
          }
          const rawData = targetDataPoint.y;
          if (ChartSeriesDataType.isAlertHistory(targetPointDetails) && rawData) {
            targetTooltipDetails.valueInfo = targetPointDetails.name;
            multiToolTipDetails.push(targetTooltipDetails);
          } else if (ChartSeriesDataType.isPointHistory(targetPointDetails)) {
            targetTooltipDetails.valueInfo = this.formatHistoryValue(rawData, labelAsBinaryUnit, unitName);
            const binValueFloor = this.getBinValueFloor(targetDataPoint);
            const binSampleSize = binValueFloor != null
              ? this.valueBasedXAxisInfo?.binStatistics?.sampleSizeBySeriesKeyByBinFloor?.get(binValueFloor)?.get(ChartSeriesDataType.buildKey(targetPointDetails)) ?? null
              : null;
            if (binSampleSize != null) {
              targetTooltipDetails.sampleSizeInfo = this.SAMPLE_SIZE_LABEL + '=' + binSampleSize;
              hasSampleSizes = true;
            }
            multiToolTipDetails.push(targetTooltipDetails);
          } else if (ChartSeriesDataType.isMeterHistory(targetPointDetails)) {
            targetTooltipDetails.valueInfo = rawData != null ? rawData.toFixed(2) + ' ' + targetPointDetails.unit : '';
            const estimate = this.getAbsoluteEstimate(targetDataPoint);
            const absoluteBreakdownTotal = this.getAbsoluteBreakdownTotal(targetDataPoint);
            if (estimate && absoluteBreakdownTotal) {
              const estimatePercent = (estimate * 100 / absoluteBreakdownTotal);
              targetTooltipDetails.estimateInfo = '(' + estimate.toFixed(2) + ' - ' + estimatePercent.toFixed(0) + '%)';
              hasEstimates = true;
            }

            if (this.showVariance && rawData != null && !isNaN(rawData)) {
              const varianceInfo = targetPointDetails.compareToSeriesInfo?.reduce((acc, compareToSeriesInfo) => {
                const info = this.findDataPointVariance(targetDataPoint, series.dataPoints, compareToSeriesInfo.id, e.chart.options.data, targetPointDetails.unit);
                if (info != null) {
                  acc.push({info: info, qualifier: compareToSeriesInfo.qualifier});
                }
                return acc;
              }, [] as MultiTooltipDetails['varianceInfo']);
              targetTooltipDetails.varianceInfo = varianceInfo?.length ? varianceInfo : null;
            }
            multiToolTipDetails.push(targetTooltipDetails);
          }
        }
      }
    });

    const multipleSeriesInTooltip = multiToolTipDetails.length > 1;
    const tooltipTableRows = multiToolTipDetails.map(tooltipDetails => {
      const cssClass = tooltipDetails.highlight && multipleSeriesInTooltip ? ' class="highlight"' : '';
      /** Information about the x datapoint. Could be timestamp in time based x axis or a number otherwise. */
      let htmlEncodedXValueInfo = '';
      if (isXAxisValueBased) {
        if (tooltipDetails.xValueInfo) {
          htmlEncodedXValueInfo = HtmlUtils.escapeHTML(tooltipDetails.xValueInfo);
        }
      } else if (tooltipDetails.timestampInfo != null) {
        htmlEncodedXValueInfo = HtmlUtils.escapeHTML(tooltipDetails.timestampInfo.ts.format(ChartTimestampUtils.getFormatString(aggregatePeriod)));
        const dlsMessage = ChartTimestampUtils.getDLSMessage(tooltipDetails.timestampInfo.info?.dlsInfo);
        if (dlsMessage) {
          htmlEncodedXValueInfo += this.createWarning(this.translateService.instant(dlsMessage));
        }
        const leapYearMessage = ChartTimestampUtils.getLeapYearMessage(tooltipDetails.timestampInfo.info?.leapYearInfo);
        if (leapYearMessage) {
          htmlEncodedXValueInfo += this.createWarning(this.translateService.instant(leapYearMessage.messageKey, leapYearMessage.parameters));
        }
      }

      let htmlEncodedVarianceInfo = '';
      const varianceInfos = tooltipDetails.varianceInfo?.filter(varianceInfo => !!varianceInfo.info);
      if (varianceInfos?.length) {
        htmlEncodedVarianceInfo = varianceInfos.map(varianceInfo => {
          return HtmlUtils.escapeHTML(varianceInfo.info)
            + (varianceInfo.qualifier ? HtmlUtils.escapeHTML(' (' + varianceInfo.qualifier + ')') : '');
        }).join('<br>');
      }

      return '<tr' + cssClass + ' style="vertical-align: top;">'
             + '  <td style="text-align: center;"><span style="color:' + HtmlUtils.escapeHTML(tooltipDetails.colour) + '">' + tooltipDetails.htmlEscapedSymbol + '</span></td>'
             + '  <td>' + HtmlUtils.escapeHTML(tooltipDetails.equipmentInfo) + '</td>'
             + '  <td>' + HtmlUtils.escapeHTML(tooltipDetails.metadataInfo) + '</td>'
             + '  <td>' + HtmlUtils.escapeHTML(tooltipDetails.functionInfo) + '</td>'
             + '  <td>' + HtmlUtils.escapeHTML(tooltipDetails.zoneInfo) + '</td>'
             + '  <td>' + htmlEncodedXValueInfo + '</td>'
             + '  <td>' + htmlEncodedVarianceInfo + '</td>'
             + '  <td style="text-align: right">' + HtmlUtils.escapeHTML(tooltipDetails.valueInfo) + '</td>'
             + '  <td>' + HtmlUtils.escapeHTML(tooltipDetails.sampleSizeInfo) + '</td>'
             + '  <td>' + HtmlUtils.italicize(HtmlUtils.escapeHTML(tooltipDetails.estimateInfo) + (tooltipDetails.estimateInfo != null ? this.NOTE_MARKER_HTML : '')) + '</td>'
             + '</tr>';
    }).join('');
    let timezoneInfoRow: string = '';
    if (multiToolTipDetails?.[0]?.timestampInfo?.ts) {
      const shortTimezone: string = moment(multiToolTipDetails[0].timestampInfo.ts).tz(this.effectiveTimezone).format('z');
      const leadingColumnCount = this.TOOLTIP_COLUMN_COUNT - this.TOOLTIP_TIMEZONE_MESSAGE_SPAN;
      timezoneInfoRow = '<tr><td colspan="' + leadingColumnCount + '"></td>'
        + '<td colspan="' + this.TOOLTIP_TIMEZONE_MESSAGE_SPAN + '">'
        + this.translateService.instant('Timezone') + ': ' + this.effectiveTimezone + ' (' + shortTimezone + ')'
        + '</td></tr>';
    }

    let estimateInfoRow: string = '';
    if (hasEstimates) {
      const message = this.translateService.instant({messageKey: 'Included estimated values'});
      estimateInfoRow = this.buildTooltipInfoRow(message, this.NOTE_MARKER_HTML);
    }

    let sampleSizeInfoRows = '';
    if (isXAxisValueBased && multiToolTipDetails.length > 0) {
      sampleSizeInfoRows = this.buildXAxisSampleSizeRow(sourcePoint, xAxisTooltipInfo);
      if (hasSampleSizes) {
        const message = this.SAMPLE_SIZE_LABEL + ' = ' + this.translateService.instant({messageKey: 'Sample size'});
        sampleSizeInfoRows += this.buildTooltipInfoRow(message);
      }
    }

    return '<table class="chartTooltip">' + tooltipTableRows + timezoneInfoRow + estimateInfoRow + sampleSizeInfoRows + '</table>';
  };

  private buildXAxisSampleSizeRow(sourcePoint: ChartDataPoint, xAxisTooltipInfo: XAxisTooltipInfo): string {
    const binValueFloor = this.getBinValueFloor(sourcePoint);
    const xAxisBinSampleSize = binValueFloor != null
      ? this.valueBasedXAxisInfo.binStatistics?.xAxisSampleSizeByBinFloor?.get(binValueFloor) ?? null
      : null;
    let xAxisSampleSizeRow = '';
    if (xAxisBinSampleSize != null && xAxisTooltipInfo.favouriteData != null) {
      const xAxisFavouriteLabel = ChartsUtil.buildEquipmentMetadataLabel(
        this.valueBasedXAxisInfo.equipment.name,
        xAxisTooltipInfo.favouriteData.favourite.metadata.name
      );
      const binInfo = xAxisFavouriteLabel + ' ' + this.buildBinValueInfo(binValueFloor, xAxisTooltipInfo);
      xAxisSampleSizeRow = this.buildTooltipInfoRow(binInfo + '  ' + this.SAMPLE_SIZE_LABEL + '=' + xAxisBinSampleSize);
    }
    return xAxisSampleSizeRow;
  }

  private buildTooltipInfoRow(message: string, markerHtml = ''): string {
    const markedMessage = markerHtml ? (markerHtml + ' ' + HtmlUtils.escapeHTML(message)) : HtmlUtils.escapeHTML(message);
    return '<tr><td colspan="' + this.TOOLTIP_COLUMN_COUNT + '"><small>' + HtmlUtils.italicize(markedMessage) + '</small></td></tr>';
  }

  private buildBinValueInfo(binValueFloor: number, xAxisTooltipInfo: XAxisTooltipInfo): string {
    let binValueInfo: string;
    if (xAxisTooltipInfo.isXAxisFavouriteBinaryUnit) {
      binValueInfo = this.formatHistoryValue(binValueFloor, true, xAxisTooltipInfo.displayedUnitName);
    } else {
      const binValueCeiling = MathUtils.roundTo(binValueFloor + xAxisTooltipInfo.binSize);
      binValueInfo = this.formatHistoryValue([binValueFloor, binValueCeiling], false, xAxisTooltipInfo.displayedUnitName);
    }
    return binValueInfo;
  }

  private findDataPointVariance(sourceDataPoint: ChartDataPoint, sourceDataPoints: ChartDataPoint[], seriesId: ChartSeriesDataType['id'],
                                series: ChartDataSeriesOptions[], unit: MeterHistoryChartSeriesData['unit']): string {
    const otherSeries = series.find(s => seriesId === this.getChartSeriesDataType(s).id);
    if (otherSeries) {
      const otherDataPoint = this.getClosestChartDataPoint(otherSeries.dataPoints, null, sourceDataPoint, sourceDataPoints, this.valueBasedXAxisInfo != null);
      const otherRawData = otherDataPoint?.y;
      if (otherRawData != null && !isNaN(otherRawData)) {
        const varianceUnits = sourceDataPoint.y - otherRawData;
        const variancePercentage = otherRawData !== 0 ? Math.round(varianceUnits / otherRawData * 100) + '%' : '';
        const prefix = varianceUnits > 0 ? '+' : '';
        return prefix + varianceUnits.toFixed(2) + ' ' + unit + ' (' + variancePercentage + ')';
      }
    }
    return null;
  }

  private createWarning(msg: string): string {
    return ChartTimestampUtils.MARKER_CHAR + ' <sup class="dlsWarning">' + HtmlUtils.escapeHTML(msg) + '</sup>';
  }

  private addGeneralChartOptions(chartOptions: ChartOptions, axisXOptions: ChartAxisXOptions, axisYOptions: ChartAxisYOptions | ChartAxisYOptions[],
                                 axisY2Options: ChartAxisYOptions | ChartAxisYOptions[], hideMenu: boolean): void {
    chartOptions['rangeChanged'] = this.rangeSyncHandler;
    chartOptions.zoomEnabled = true;
    chartOptions.exportEnabled = !hideMenu;
    chartOptions.exportFileName = 'CIM Point History';

    chartOptions.toolTip = {
      shared: true,
      contentFormatter: this.tooltipContentFormatter
    };

    chartOptions.legend = {
      fontSize: this.splitMode ? 10 : 12,
      cursor: 'pointer',
      verticalAlign: 'bottom',
      horizontalAlign: 'center'
    };

    let secondXAxis = false;
    if (chartOptions.data.length > 1) {
      chartOptions.legend.itemclick = (event: ChartEvent) => {
        // Chart blows up when all series are set to not visible and the x-axis has minimum and maximum set. So when the last series has
        // been made not visible, remove the minimum and maximum for the x-axis. Then, if the first series has been made visible again, set
        // the minimum and maximum for the x-axis
        const haveAnyVisibleSeriesBeforeChange = event.chart.options.data.some(this.isSeriesVisible);
        event.dataSeries.visible = !this.isSeriesVisible(event.dataSeries);
        const haveAnyVisibleSeriesNow = event.chart.options.data.some(this.isSeriesVisible);
        if (haveAnyVisibleSeriesBeforeChange !== haveAnyVisibleSeriesNow) {
          this.toggleAxisXBounds();
        }
        event.chart.render();
      };
      secondXAxis = chartOptions.data.some(dataSeriesOptions => dataSeriesOptions.axisXIndex > 0);
    }

    chartOptions.axisX = [];
    chartOptions.axisX[0] = axisXOptions;
    if (secondXAxis) {
      chartOptions.axisX[1] = Object.assign({}, axisXOptions);
    }
    chartOptions.axisY = axisYOptions;
    chartOptions.axisY2 = axisY2Options;
  }

  private isSeriesVisible: (series: ChartDataSeriesOptions) => boolean = (series: ChartDataSeriesOptions) => {
    // The 'visible' property defaults to true, so treat undefined as a true value. Therefore, if a series with undefined visible
    // property has been clicked, it should be made invisible.
    return typeof series.visible === 'undefined' || series.visible;
  };

  rangeSyncHandler = (rangeChangedEvent: ChartRangeEvent) => {
    this.rangeChangedEvent.next(rangeChangedEvent);
  };

  private getAxisXArray(chartOptions: ChartOptions): ChartAxisXOptions[] {
    if (chartOptions.axisX) {
      return Array.isArray(chartOptions.axisX) ? chartOptions.axisX : [chartOptions.axisX];
    } else {
      this.logger.error('The x-axis is null');
      return [];
    }
  }

  private toggleAxisXBounds(): void {
    const seriesOptionsByAxisIndex = CollectionUtils.groupByFn(this.chartOptions.data, (d) => d.axisXIndex ?? 0);
    const isXAxisValueBased = this.valueBasedXAxisInfo != null;
    const valueBounds = this.valueBasedXAxisInfo?.binStatistics?.valueBounds;
    const haveBounds = isXAxisValueBased
      ? valueBounds?.minimum != null && valueBounds?.maximum != null
      : !!(this.startDate && this.endDate);
    // X-axis is an array when using meter streams and comparing to previous period
    this.getAxisXArray(this.chartOptions).forEach((xAxisOptions, axisIndex)  => {
      const seriesOptions = seriesOptionsByAxisIndex.get(axisIndex);
      let visibleWithData = false;
      let multipleDataPoints = false;
      let noLabelPointsPresent = false;
      let minimum: number = null;
      let maximum: number = null;
      seriesOptions.forEach(so => {
        if (this.isSeriesVisible(so) && !!so?.dataPoints?.length) {
          // Setting minimum and maximum together seems to blow up unless there is data on the chart. So ensure there is data,
          // before setting minimum and maximum
          visibleWithData = true;
          // Making an assumption here that if the first data point does not have a label, neither do any other data points
          noLabelPointsPresent = noLabelPointsPresent || so.dataPoints[0]?.label == null;
          if (so?.dataPoints?.length > 1) {
            multipleDataPoints = true;
          } else if (so?.dataPoints?.length === 1) {
            // If all series have single data point, need to work around Chart JS bug https://canvasjs.com/forums/topic/chart-hangs-with-this-code/
            // By setting minimum and maximum, so need to keep track of minimum and maximum values across the series
            const xValue = so.dataPoints[0].x;
            const valueAsNumber = xValue instanceof Date ? xValue.valueOf() : xValue;
            minimum = minimum == null ? valueAsNumber : Math.min(minimum, valueAsNumber);
            maximum = maximum == null ? valueAsNumber : Math.max(maximum, valueAsNumber);
          }
        }
      });

      if (!visibleWithData) {
        delete xAxisOptions.minimum;
        delete xAxisOptions.maximum;
      } else if (multipleDataPoints) {
        if (noLabelPointsPresent) {
          if (haveBounds) {
            if (isXAxisValueBased) {
              this.setValueBasedXAxisBounds(xAxisOptions);
            } else {
              xAxisOptions.minimum = this.startDate.valueOf();
              xAxisOptions.maximum = this.endDate.valueOf();
            }
          } else {
            this.logger.error('Cannot set minimum and maximum for x-axis as start and end dates are not set. Start date', this.startDate, 'End date', this.endDate);
          }
        } else {
          // Only data points with label are present. In case minimum and maximum have been set, e.g. because the data
          // was loaded progressively and the chart only had a single data point present, remove them
          delete xAxisOptions.minimum;
          delete xAxisOptions.maximum;
        }
      } else {
        if (noLabelPointsPresent && haveBounds) {
          if (isXAxisValueBased) {
            this.setValueBasedXAxisBounds(xAxisOptions);
          } else {
            xAxisOptions.minimum = this.startDate.valueOf();
            xAxisOptions.maximum = this.endDate.valueOf();
          }
        } else if (minimum != null && maximum != null) {
          xAxisOptions.minimum = minimum - 1;
          xAxisOptions.maximum = maximum + 1;
        } else {
          this.logger.error('Series have single data points but could not work out minimum or maximum values. Chart will likely hang. Minimum', minimum, 'Maximum', maximum);
        }
      }
    });
  }

  /** Sets the value-based x-axis bounds. Pads by 1 when min and max are the same value (ex: binary, big bin size),
   * because CanvasJS hangs when minimum === maximum on a value axis. https://canvasjs.com/forums/topic/chart-hangs-with-this-code/ */
  private setValueBasedXAxisBounds(xAxisOptions: ChartAxisXOptions): void {
    const valueBounds = this.valueBasedXAxisInfo?.binStatistics?.valueBounds;
    const minimum = valueBounds?.minimum ?? null;
    const maximum = valueBounds?.maximum ?? null;
    if (minimum != null && maximum != null && minimum === maximum) {
      xAxisOptions.minimum = minimum - 1;
      xAxisOptions.maximum = maximum + 1;
    } else {
      xAxisOptions.minimum = minimum;
      xAxisOptions.maximum = maximum;
    }
  }

  private switchChartSplitModeEvent(splitMode: any): void {
    this.splitModeEvent.emit(splitMode);
  }

  private refreshChart(showLoading: boolean): void {
    this.chartLoading = showLoading;

    if (this.chartOptions != null) {
      if (this.chartOptions.axisX != null) {
        // Ensure minimum and maximum are set on x-axis if possible
        this.toggleAxisXBounds();
      }

      if (showLoading) {
        this.chartOptions.subtitles = [{text: this.translateService.instant('Loading...'), verticalAlign: 'top'}];
      } else {
        this.chartOptions.subtitles = [];
        // Set chart title if we have one
        if (this.chartTitle) {
          this.chartOptions.title = {text: this.chartTitle, fontSize: this.splitMode ? 13 : 15};
        }
      }
    }

    // Call render() on the chart so that it redraws itself with the latest data
    // Need to render the chart before the code below is run, so that the axis are initialised
    if (this.chart) {
      this.chart.render();
    }

    this.checkAndFinaliseChart(showLoading);
  }

  private checkAndFinaliseChart(showLoading: boolean): void {
    if (!showLoading && this.chart) {
      this.updateChartMenu();
      this.adjustYAxisFormatString();
      this.chartCompleted.next();
    }
  }

  private updateChartMenu(): void {
    /* Add buttons to the Export Menu for exportCSV and toggle split charts */
    const extendedToolbar = this.getChartMenu();
    // Need to ensure that the menu overlays other charts. Otherwise, when multiple charts are shown, and the menu is longer than the chart
    // it belongs to, it appears behind the other charts. It needs to be in front.
    if (extendedToolbar) {
      extendedToolbar.style['z-index'] = 10;
      extendedToolbar.style.fontFamily = 'inherit';
      // Only extend the menu once
      if (!extendedToolbar.querySelector('[' + ChartsUtil.CHART_MENU_DEFAULT_DATA_ATTRIBUTE + ']')) {
        // add "Split None" if splitting by something
        if (this.splitMode) {
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Combined Chart'),
            this.translateService.instant('Combined chart tooltip'), 'afterbegin', () => this.switchChartSplitModeEvent(null));
        }

        this.allowedSplitModes.forEach(splitMode => {
          if (this.splitMode !== splitMode) {
            const message = this.menuItemLabels?.get(splitMode);
            if (message) {
              ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant(message.label.messageKey, message.label.parameters),
                this.translateService.instant(message.tooltip.messageKey, message.tooltip.parameters), 'afterbegin',
                () => this.switchChartSplitModeEvent(splitMode));
            } else {
              this.logger.error('Cannot find a translation for menu item', splitMode);
            }
          }
        });
        if (this.valueBasedXAxisInfo == null) {
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Pivot Data Table'), '', 'beforeend', () => this.showDataTable());
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Export CSV Flat'), '', 'beforeend', () => this.exportToCSVFlat());
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Export CSV Pivot'), '', 'beforeend', () => this.exportToCSVPivot());
        } else {
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Pivot Data Table'), '', 'beforeend', () => this.showValueBasedDataTable());
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Export CSV Pivot'), '', 'beforeend', () => this.exportValueBasedCSVPivot());
        }
        if (this.availableChartTypes?.length) {
          this.availableChartTypeOptions = this.availableChartTypes.map(v => ({value: v, label: this.translateService.instant(v as string)}));
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Select Chart Type'), '', 'beforeend', () => this.selectChartType());
        }
        if (this.chartHasEstimates) {
          const estimateFlagsLabel = this.showEstimateFlags ? 'Hide Estimate Flags' : 'Show Estimate Flags';
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant(estimateFlagsLabel), '', 'beforeend', () => this.toggleEstimateFlags());
        }

        if (this.valueBasedXAxisInfo == null && this.availableTimezones?.length) {
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Select Timezone'), '', 'beforeend', () => this.selectTimezone());
        }
      }

      if (this.createFullSizeChartMenuItem) {
        this.createFullSizeChartMenuItem = false;
        this.addMenuOptionForFullSize(extendedToolbar);
      }
    } else if (!this.hideMenu) {
      this.logger.error('Cannot find the toolbar menu. Cannot add menu options, such as split and export menu items.');
    }
  }

  private adjustYAxisFormatString(): void {
    if (this.chart) {
      const primaryAxisY = this.chart.axisY?.[0];
      if (primaryAxisY) {
        const interval = primaryAxisY.interval;
        if (interval != null && interval < 1) {
          const decimals = MathUtils.countDecimals(interval);
          if (decimals != 0) {
            primaryAxisY.set('valueFormatString', '#0.' + '#'.repeat(decimals), true);
          }
        }
      }
    }
  }

  private exportToCSVFlat(): void {
    const axisXViewportBoundsByAxisIndex = this.getXAxisViewportBoundsByAxisIndex();
    const unitsBySeriesId = new Map<number | string, string>();
    const seriesToExport = this.getSeriesToExport();
    seriesToExport.forEach(series => {
      const seriesData = series.seriesData;
      unitsBySeriesId.set(seriesData.id.toString(), this.getSeriesDataUnit(seriesData));
    });
    const flatData = this.exportDataBuilder.buildFlatData(seriesToExport, unitsBySeriesId, axisXViewportBoundsByAxisIndex, this.workingHourBoundsByWeekday);
    const columns = this.makeCSVColumns(flatData.keys, flatData.headers);
    const filename = this.generateExportCSVFilename(flatData.bounds?.start, flatData.bounds?.end);
    this.csvExportService.downloadCsvData(flatData.data, { filename, columns, header: true });
  }

  private getSeriesDataUnit(seriesData: ChartSeriesDataType): string {
    return ChartSeriesDataType.isPointHistory(seriesData)
      ? this.favouritesById.get(+seriesData.id).getUnitName()
      : ChartSeriesDataType.isMeterHistory(seriesData)
        ? seriesData.unit
        : ChartSeriesDataType.isAlertHistory(seriesData)
          ? (ALERT_FAULT + '/' + ALERT_NON_FAULT)
          : '';
  }

  private getSeriesToExport(): MiniChartDataSeriesOptions[] {
    const seriesToExport: MiniChartDataSeriesOptions[] = [];
    this.chart.options.data?.forEach(series => {
      if (series.dataPoints?.length && this.isSeriesVisible(series)) {
        const seriesData = this.getChartSeriesDataType(series);
        seriesToExport.push({
          ...series,
          seriesData,
          dataPoints: this.exportDataBuilder.prepareSeriesDataPointsForExport(seriesData, series.dataPoints)
        });
      }
    });
    return seriesToExport;
  }

  private showDataTable(): void {
    const p = this.getCSVPivotConfig();
    p.then(exportConfig => {
      this.openDataTableModal(this.exportDataBuilder.buildPivotData(exportConfig, this.getXAxisViewportBoundsByAxisIndex(), this.workingHourBoundsByWeekday));
    }).catch(reason => {
      this.logger.debug('Not showing Data Table. Reason:', reason);
    });
  }

  private openDataTableModal(pivotData: CSVPivotData): void {
    if (pivotData != null) {
      const modalRef = this.modalService.open(DataTableComponent, {windowClass: 'modal-xxl', centered: true});
      const dataTableComponent: DataTableComponent = modalRef.componentInstance;
      dataTableComponent.title = this.chart.options && this.chart.options.title ? this.chart.options.title.text : '';
      dataTableComponent.headers = pivotData.headers;
      dataTableComponent.keys = pivotData.keys;
      dataTableComponent.data = pivotData.data;
      dataTableComponent.dataTimestampBounds = pivotData.bounds ?? null;
      dataTableComponent.csvExportFn = (csvPivotData: CSVPivotData) => this.doCSVExportPivot(csvPivotData);
    }
  }

  private exportToCSVPivot(): void {
    const p = this.getCSVPivotConfig();
    p.then(exportConfig => {
      this.performExportToCSVPivot(exportConfig);
    }).catch(reason => {
      this.logger.debug('Not exporting CSV. Reason:', reason);
    });
  }

  private showValueBasedDataTable(): void {
    this.openDataTableModal(this.resolveValueBasedPivotData());
  }

  private exportValueBasedCSVPivot(): void {
    this.doCSVExportPivot(this.resolveValueBasedPivotData());
  }

  /** Rows and columns of the value based data table, or null while the x axis favourite data has not finished loading. */
  private resolveValueBasedPivotData(): CSVPivotData | null {
    const binStatistics = this.valueBasedXAxisInfo?.binStatistics;
    const xAxisFavouriteData = binStatistics != null ? this.favouritesById.get(this.valueBasedXAxisInfo.favouriteId) : null;
    let pivotData: CSVPivotData = null;
    if (xAxisFavouriteData != null) {
      const valueBasedPivotConfig: ValueBasedPivotConfig = {
        series: this.getSeriesToExport(),
        xAxisSampleSizeByBinFloor: binStatistics.xAxisSampleSizeByBinFloor,
        sampleSizeBySeriesKeyByBinFloor: binStatistics.sampleSizeBySeriesKeyByBinFloor,
        xAxisEquipmentName: this.valueBasedXAxisInfo.equipment.name,
        xAxisMetadataName: xAxisFavouriteData.favourite.metadata.name,
        xAxisUnitName: xAxisFavouriteData.getUnitName(),
        viewportBounds: this.getXAxisViewportBoundsByAxisIndex().get(0) ?? null
      };
      pivotData = this.exportDataBuilder.buildValueBasedPivotData(valueBasedPivotConfig);
    } else {
      this.logger.debug('No value based pivot data. Reason: no x axis favourite bin counts or favourite data.', this.valueBasedXAxisInfo);
    }
    return pivotData;
  }

  private toggleEstimateFlags(): void {
    this.showEstimateFlags = !this.showEstimateFlags;
    this.showEstimateFlagsEvent.next(this.showEstimateFlags);
  }

  private selectChartType(): void {
    this.modalService.open(this.chartTypePrompt, {size: 'sm', centered: true, keyboard: true}).result.then((selectedChartType: JS_CHART_SERIES_TYPE) => {
      this.chartType = selectedChartType;
      this.selectedChartTypeEvent.next(this.chartType);
    }, (_reason) => {
    });
  }

  private selectTimezone(): void {
    this.modalService.open(this.timezonePrompt, {size: 'md', centered: true, keyboard: true}).result.then((timezone: string) => {
      this.timezone = timezone || null;
      this.selectedTimezone.next(this.timezone);
    }, (_reason) => {
    });
  }

  private getCSVPivotConfig(): Promise<CSVPivotConfig> {
    const seriesToExport = this.getSeriesToExport();
    const resolvedPivotConfig = this.exportDataBuilder.resolvePivotConfig(seriesToExport);
    return new Promise((resolve, reject) => {
      if (resolvedPivotConfig) {
        if ('period' in resolvedPivotConfig) {
          resolve(resolvedPivotConfig);
        } else {
          IntervalPromptModalComponent.open(
            this.modalService,
            {message: {messageKey: 'Enter interval between data points for export'}}
          ).result.then((dataInterval: number) => {
            resolve({...resolvedPivotConfig, period: {amount: dataInterval, unit: 'minutes'}});
          }, (reason) => {
            reject(reason);
          });
        }
      } else {
        reject('No series to export');
      }
    });
  }

  private performExportToCSVPivot(pivotConfig: CSVPivotConfig): void {
    const pivotData = this.exportDataBuilder.buildPivotData(pivotConfig, this.getXAxisViewportBoundsByAxisIndex(), this.workingHourBoundsByWeekday);
    this.doCSVExportPivot(pivotData);
  }

  private doCSVExportPivot(pivotData: CSVPivotData): void {
    if (pivotData) {
      const filename = this.generateExportCSVFilename(pivotData.bounds?.start, pivotData.bounds?.end);
      const columns: ColumnOption[] = this.makeCSVColumns(pivotData.keys, pivotData.headers);
      this.csvExportService.downloadCsvData(pivotData.data, { filename, columns, header: true });
    }
  }

  private makeCSVColumns(keys: string[], headers: string[] | null): ColumnOption[] {
    const hasHeader = !!headers?.length;
    return keys.map((key, index) => ({
      key,
      ...hasHeader && { header: headers[index] }
    }));
  }

  private generateExportCSVFilename(viewportMinimum: number, viewportMaximum: number): string {
    const startBound = (viewportMinimum == null) ? this.startDate : moment(viewportMinimum).utc();
    const endBound = (viewportMaximum == null) ? this.endDate : moment(viewportMaximum).utc();
    return 'History ' + startBound.format(this.TIMESTAMP_FORMAT) + ' to ' + endBound.format(this.TIMESTAMP_FORMAT);
  }

  private getXAxisViewportBoundsByAxisIndex(): Map<number, ViewportBounds> {
    return new Map((this.chart.axisX || []).map((axisX, index) =>
      [index, {viewportMinimum: axisX.viewportMinimum, viewportMaximum: axisX.viewportMaximum}]));
  }


  private generateWorkingHourStripLines(startDate: Moment,
                                        endDate: Moment,
                                        timezone: string,
                                        workHourBoundsByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig>): ChartStripLinesOptions[] {
    const stripLines: ChartStripLinesOptions[] = [];
    const startTs: Moment = startDate.clone().tz(timezone);
    const endTs: Moment = endDate.clone().tz(timezone);
    while (startTs.isSameOrBefore(endTs)) {
      const currentDay: IsoWeekday = startTs.isoWeekday();
      if (workHourBoundsByWeekday.has(currentDay)) {
        const workingHoursConfig = workHourBoundsByWeekday.get(currentDay);
        const endTimeBound = startTs.clone().set(workingHoursConfig.end);
        if (workingHoursConfig.end.hour === 0 && workingHoursConfig.end.minute === 0 && workingHoursConfig.end.second === 0) {
          // moment is mutable
          endTimeBound.add(1, 'day');
        }
        stripLines.push({
          startValue: startTs.clone().set(workingHoursConfig.start).valueOf(),
          endValue: endTimeBound.valueOf(),
          color: this.WORKING_HOURS_COLOR,
          labelWrap: undefined, // Use default
          labelMaxWidth: undefined // Use default
        });
      }
      startTs.add(1, 'day');
    }
    return stripLines;
  }

  private getChartMenu(): HTMLElement {
    return this.chartContainer.nativeElement.childNodes[0].querySelector('.canvasjs-chart-toolbar').childNodes[3];
  }

  private addMenuOptionForFullSize(extendedToolbar?: HTMLElement): void {
    if (!this.splitMode) {
      extendedToolbar = extendedToolbar == null ? this.getChartMenu() : extendedToolbar;
      if (extendedToolbar) {
        // Only add the menu item if one does not already exist
        if (!extendedToolbar.querySelector('[' + ChartsUtil.CHART_MENU_DYNAMIC_HEIGHT_DATA_ATTRIBUTE + ']')) {
          ChartsUtil.addMenuOption(extendedToolbar, this.translateService.instant('Full Size'), this.translateService.instant('Full Size tooltip'),
            'afterbegin', () => {
              if (!this.splitMode) {
                this.chartHeight = this.getChartHeightForKey(CHART_HEIGHT.DYNAMIC);
                this.sizeChanged = true;
                // Fire an event to indicate the height has been restored to default
                this.resizeEvent.emit({status: RESIZE_EVENT_STATUS.STOPPED, height: null});
              }

              const removed = this.removeMenuOptionForFullSize(extendedToolbar);
              if (removed) {
                const activeElement = document.activeElement;
                if (activeElement instanceof HTMLElement) {
                  // Close the chart menu
                  activeElement.blur();
                } else {
                  this.logger.error('Cannot close the Chart Menu as active element is not a HTMLElement', activeElement);
                }
              } else {
                this.logger.error('Cannot remove the Chart Menu item for Full Size as cannot find the element in the DOM');
              }
            }, ChartsUtil.CHART_MENU_DYNAMIC_HEIGHT_DATA_ATTRIBUTE);
        }
      } else if (!this.hideMenu) {
        this.logger.error('Cannot find the chart menu bar. Will not add menu option to allow to return back to dynamic height');
      }
    }
  }

  private removeMenuOptionForFullSize(extendedToolbar: HTMLElement): boolean {
    const el = extendedToolbar?.querySelector('[' + ChartsUtil.CHART_MENU_DYNAMIC_HEIGHT_DATA_ATTRIBUTE + ']');
    if (el) {
      el.remove();
      return true;
    } else {
      return false;
    }
  }

  private convertReferenceLinetoChartStripLine(referenceLine: ReferenceLine): ChartStripLinesOptions {
    return {
      value: referenceLine.getNumberValue(this.site.timezone), // The reference lines are always in site's timezone
      color: '#000',
      labelFontColor: '#000',
      labelPlacement: 'inside',
      labelAlign: 'center',
      labelBackgroundColor: '#fff',
      label: referenceLine.getLabel(),
      lineDashType: 'dash',
      showOnTop: true,
      labelWrap: undefined, // Use default
      labelMaxWidth: undefined // Use default
    };
  }

  private setWorkingHoursAndReferenceLinesStripLines(chartOptions: ChartOptions, addBinaryStripLine: boolean) {
    if (chartOptions) {
      const axisXStripLines: ChartStripLinesOptions[] = [];
      const axisYStripLines: ChartStripLinesOptions[] = [];

      if (addBinaryStripLine) {
        // Cannot use the backgroundColor property of the chart as it colours the whole back ground, not just the area where the data
        // is shown. The CanvasJS authors recommend using a strip line to achieve this
        axisXStripLines.push({startValue: this.startDate.valueOf(), endValue: this.endDate.valueOf(), color: '#F7F7F7', showOnTop: false,
          labelWrap: undefined, labelMaxWidth: undefined});
      }

      if (this.workingHourBoundsByWeekday) {
        axisXStripLines.push(...this.generateWorkingHourStripLines(this.startDate, this.endDate, this.site.timezone, this.workingHourBoundsByWeekday));
      }

      if (this.referenceLines?.length) {
        this.referenceLines.forEach(referenceLine => {
          if (referenceLine.getOrientation() === Orientation.Vertical) {
            axisXStripLines.push(this.convertReferenceLinetoChartStripLine(referenceLine));
          } else if (referenceLine.getOrientation() === Orientation.Horozontal) {
            axisYStripLines.push(this.convertReferenceLinetoChartStripLine(referenceLine));
          }
        });
      }

      const axisXArray = this.getAxisXArray(chartOptions);
      if (axisXArray?.length) {
        axisXArray[0].stripLines = axisXStripLines;
      }

      // Check for array to keep TypeScript happy
      if (chartOptions.axisY && !Array.isArray(chartOptions.axisY)) {
        chartOptions.axisY.stripLines = axisYStripLines;
      }
    }
  }

  private getClosestChartDataPoint(dataPoints: ChartDataPoint[],
                                   targetTs: number,
                                   sourcePoint: ChartDataPoint,
                                   sourceDataPoints: ChartDataPoint[],
                                   isXAxisValueBased: boolean): ChartDataPoint {
    if (!isXAxisValueBased && sourcePoint.label != null) {
      // When ChartDataPoints have a label their x value represents their index in the series
      const currentPointIndex = sourcePoint.x;
      if (!(currentPointIndex instanceof Date)) {
        // As the data is retrieved latest first, different data series could be of different lengths at any given time,
        // so need to calculate the index from the end of each series
        const currentPointIndexFromEnd = sourceDataPoints.length - currentPointIndex;
        const index = dataPoints.length - currentPointIndexFromEnd;
        if (index >= 0 && index < dataPoints.length) {
          return dataPoints[index];
        } else {
          return null;
        }
      } else {
        throw new Error('The x value is not a number when label is set');
      }
    } else {
      const compareToSourcePoint: (point: ChartDataPoint) => number = isXAxisValueBased
        ? point => <number>sourcePoint.x - <number>point.x
        : point => targetTs - moment(point.x).valueOf();
      const si = SearchUtils.sortedIndex(dataPoints, compareToSourcePoint);
      if (si <= 0) {
        return dataPoints[0];
      } else if (si >= dataPoints.length) {
        return dataPoints[dataPoints.length - 1];
      } else {
        const diffToPoint = Math.abs(compareToSourcePoint(dataPoints[si]));
        const diffToPreviousPoint = Math.abs(compareToSourcePoint(dataPoints[si - 1]));
        return diffToPoint < diffToPreviousPoint ? dataPoints[si] : dataPoints[si - 1];
      }
    }
  }

  private isResizeInProgress(): boolean {
    return this.resizeInProgress || this.externalResizeInProgress;
  }

  private getResizeCoordsTopOffset(): number {
    if (this.resizeStartCoordsTopOffset == null) {
      const mainContainerElements = document.getElementsByClassName(AppConstants.MAIN_CONTAINER_CLASS_NAME);
      if (mainContainerElements && mainContainerElements.length === 1) {
        this.resizeStartCoordsTopOffset = window.scrollY + mainContainerElements.item(0).getBoundingClientRect().top;
      } else {
        this.resizeStartCoordsTopOffset = this.RESIZE_DEFAULT_SCROLL_OFFSET;
        this.logger.error('Cannot find a single main-container element. Using '
          + this.resizeStartCoordsTopOffset + ' as top offset. The found elements are', mainContainerElements);
      }
    }
    return this.resizeStartCoordsTopOffset;
  }

  private getX(p: LabelChartDataPoint): number | Date {
    return p.label != null ? p.xValue : p.x;
  }

  private getAbsoluteEstimate(p: LabelChartDataPoint): number | null {
    return p.absoluteEstimate ?? null;
  }

  private getAbsoluteBreakdownTotal(p: LabelChartDataPoint): number | null {
    return p.absoluteBreakdownTotal ?? null;
  }

  private getBinValueFloor(dataPoint: LabelChartDataPoint): number | null {
    const xValue = this.getX(dataPoint);
    return typeof xValue === 'number' ? xValue : null;
  }

  private listenForResizeEvents(): void {
    new ResizeObservable(this.chartContainer.nativeElement)
      .pipe(debounceTime(AppConstants.RESIZE_EVENT_DEBOUNCE_MS), takeUntil(this.ngUnsubscribe))
      .subscribe(entries => {
        if (entries?.length) {
          this.chart?.render();
        }
      });
  }

  private getChartHeightTypeFromChartClass(chartClass: CHART_CSS_CLASS): CHART_HEIGHT {
    switch (chartClass) {
      case CHART_CSS_CLASS.BINARY_AND_ALERT:
        return CHART_HEIGHT.BINARY_AND_ALERT;
      case CHART_CSS_CLASS.BINARY_ONLY:
        return CHART_HEIGHT.BINARY_ONLY;
      case CHART_CSS_CLASS.SPLIT:
        return CHART_HEIGHT.SPLIT;
      case CHART_CSS_CLASS.FULL_BROWSER_HEIGHT:
        return CHART_HEIGHT.FULL_BROWSER_HEIGHT;
      default:
        return CHART_HEIGHT.DYNAMIC;
    }
  }

  private formatHistoryValue(rawData: number | [number, number], isBinaryUnit: boolean, unitName: string): string {
    let value: string;
    if (rawData == null) {
      value = this.translateService.instant('N/A');
    } else if (isBinaryUnit) {
      const targetBinaryUnitsArray = unitName.split('/');
      if (targetBinaryUnitsArray && targetBinaryUnitsArray.length === 2) {
        const binaryData = rawData === 0 ? targetBinaryUnitsArray[1] : targetBinaryUnitsArray[0];
        value = binaryData + ' (' + rawData + ')';
      }
    }
    if (!value) {
      const valueDisplay: string | number = Array.isArray(rawData)
        ? this.translateService.instant('start to end', {start: rawData[0], end: rawData[1]})
        : rawData;
      value = valueDisplay + ' ' + unitName;
    }
    return value;
  }
}
